import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Plus, X, FileText, Search, Pencil, Trash2, Paperclip, Scale, Target, Coins, ClipboardCheck, Printer, History } from "lucide-react";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";

const card = "bg-white rounded-2xl border border-slate-100";

const LV_CHIP = { SP1: "bg-amber-100 text-amber-700", SP2: "bg-orange-100 text-orange-700", SP3: "bg-red-100 text-red-700" };
const LV_DOT = { SP1: "bg-amber-50 text-amber-600", SP2: "bg-orange-50 text-orange-600", SP3: "bg-red-50 text-red-600" };
const ST_LABEL = { draft: "Draft", aktif: "Aktif", kedaluwarsa: "Kedaluwarsa", dicabut: "Dicabut" };
const ST_CHIP = { draft: "bg-slate-100 text-slate-500", aktif: "bg-emerald-100 text-emerald-700", kedaluwarsa: "bg-slate-100 text-slate-500", dicabut: "bg-red-100 text-red-600" };
const FU_LABEL = { dalam_pemantauan: "Dalam Pemantauan", selesai_dipantau: "Selesai Dipantau", eskalasi: "Eskalasi" };
const FU_CHIP = { dalam_pemantauan: "bg-amber-100 text-amber-700", selesai_dipantau: "bg-emerald-100 text-emerald-700", eskalasi: "bg-red-100 text-red-600" };

const TABS = [
  { id: "semua", label: "Semua" },
  { id: "draft", label: "Draft" },
  { id: "aktif", label: "Aktif" },
  { id: "kedaluwarsa", label: "Kedaluwarsa" },
  { id: "dicabut", label: "Dicabut" },
];

const emptyForm = { user_id: "", level: "SP1", issued_date: "", valid_until: "", violation: "", rule_basis: "", attachment_url: null, attachment_name: "", improvement_target: "", sanction_amount: "", publish: true };

function SpForm({ initial, employees, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(isEdit ? {
    user_id: initial.user_id, level: initial.level, issued_date: initial.issued_date, valid_until: initial.valid_until,
    violation: initial.violation || "", rule_basis: initial.rule_basis || "", attachment_url: initial.attachment_url || null,
    attachment_name: initial.attachment_name || "", improvement_target: initial.improvement_target || "",
    sanction_amount: initial.sanction_amount || "", publish: initial.status === "aktif",
  } : { ...emptyForm, issued_date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      setForm((s) => ({ ...s, attachment_url: data.url, attachment_name: f.name }));
      toast.success("Lampiran terunggah");
    } catch { toast.error("Gagal mengunggah lampiran"); }
  };

  const submit = async () => {
    if (!form.user_id) return toast.error("Pilih karyawan terlebih dahulu");
    if (!form.issued_date || !form.valid_until) return toast.error("Tanggal terbit dan berlaku sampai wajib diisi");
    if (form.valid_until <= form.issued_date) return toast.error("Berlaku sampai harus setelah tanggal terbit");
    if (form.violation.trim().length < 5) return toast.error("Uraian pelanggaran wajib diisi");
    setBusy(true);
    try {
      const payload = { ...form, sanction_amount: parseInt(form.sanction_amount || 0, 10) || 0, attachment_name: form.attachment_name || null };
      if (isEdit) await api.put(`/admin/warnings/${initial.id}`, payload);
      else await api.post("/admin/warnings", payload);
      toast.success(isEdit ? "Surat peringatan diperbarui" : payload.publish ? "Surat peringatan diterbitkan" : "SP disimpan sebagai draft");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="spf-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-display text-base font-bold text-slate-900">{isEdit ? "Edit Surat Peringatan" : "Buat Surat Peringatan"}</h3>
          <button data-testid="spf-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Karyawan <span className="text-red-500">*</span></label>
              <select data-testid="spf-employee" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className={inpL}>
                <option value="">Pilih karyawan</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{`${emp.name} — ${emp.role_label}`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Tingkat SP <span className="text-red-500">*</span></label>
              <div className="flex gap-1.5 mt-1">
                {["SP1", "SP2", "SP3"].map((lv) => (
                  <button key={lv} data-testid={`spf-level-${lv}`} onClick={() => setForm({ ...form, level: lv })}
                    className={`flex-1 h-9 rounded-xl text-xs font-bold border transition-colors ${form.level === lv ? `${LV_CHIP[lv]} border-transparent` : "border-slate-200 text-slate-400 hover:border-slate-300"}`}>{lv}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Tanggal Terbit <span className="text-red-500">*</span></label>
              <input data-testid="spf-issued" type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} className={inpL} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Berlaku Sampai <span className="text-red-500">*</span></label>
              <input data-testid="spf-valid" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className={inpL} />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Pelanggaran <span className="text-red-500">*</span></label>
            <textarea data-testid="spf-violation" value={form.violation} onChange={(e) => setForm({ ...form, violation: e.target.value })} rows={2} placeholder="Uraikan pelanggaran yang dilakukan karyawan..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Dasar Aturan</label>
            <input data-testid="spf-rule" value={form.rule_basis} onChange={(e) => setForm({ ...form, rule_basis: e.target.value })} placeholder="mis. Peraturan Perusahaan • Pasal 4" className={inpL} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Target Perbaikan</label>
            <textarea data-testid="spf-target" value={form.improvement_target} onChange={(e) => setForm({ ...form, improvement_target: e.target.value })} rows={2} placeholder="Target perbaikan yang harus dicapai karyawan..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Potongan Sanksi (Rp) — catatan saja</label>
              <input data-testid="spf-sanction" type="number" min="0" value={form.sanction_amount} onChange={(e) => setForm({ ...form, sanction_amount: e.target.value })} placeholder="0" className={inpL} />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500">Lampiran (opsional)</label>
              <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} />
              <button data-testid="spf-attach" onClick={() => fileRef.current?.click()} className="w-full h-10 px-3 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500 hover:border-[#7F1D2D] hover:text-[#7F1D2D] flex items-center gap-2 transition-colors">
                <Paperclip className="w-3.5 h-3.5" /> <span className="truncate">{form.attachment_name || "Unggah bukti / laporan"}</span>
              </button>
            </div>
          </div>
          {!isEdit && (
            <label className="flex items-center gap-2.5 cursor-pointer">
              <button type="button" data-testid="spf-publish" onClick={() => setForm({ ...form, publish: !form.publish })}
                className={`w-10 h-6 rounded-full transition-colors relative ${form.publish ? "bg-[#7F1D2D]" : "bg-slate-200"}`}>
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${form.publish ? "left-5" : "left-1"}`} />
              </button>
              <span className="text-xs font-semibold text-slate-600">{form.publish ? "Terbitkan langsung — karyawan bisa melihat SP ini" : "Simpan sebagai Draft — belum terlihat karyawan"}</span>
            </label>
          )}
          <button data-testid="spf-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>
            {busy ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : form.publish ? "Terbitkan SP" : "Simpan Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FollowUpModal({ item, onClose, onSaved }) {
  const fu = item.follow_up || {};
  const [form, setForm] = useState({ status: fu.status || "dalam_pemantauan", next_eval_date: fu.next_eval_date || "", note: fu.note || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/warnings/${item.id}/follow-up`, { ...form, next_eval_date: form.next_eval_date || null });
      toast.success("Tindak lanjut tercatat");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="spfu-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-display text-base font-bold text-slate-900">Catat Tindak Lanjut • {item.number}</h3>
          <button data-testid="spfu-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Status Pemantauan</label>
            <div className="flex gap-1.5 mt-1">
              {Object.entries(FU_LABEL).map(([k, lb]) => (
                <button key={k} data-testid={`spfu-status-${k}`} onClick={() => setForm({ ...form, status: k })}
                  className={`flex-1 h-9 rounded-xl text-[11px] font-bold border transition-colors ${form.status === k ? `${FU_CHIP[k]} border-transparent` : "border-slate-200 text-slate-400"}`}>{lb}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Evaluasi Berikutnya</label>
            <input data-testid="spfu-eval" type="date" value={form.next_eval_date} onChange={(e) => setForm({ ...form, next_eval_date: e.target.value })} className={inpL} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Catatan HR</label>
            <textarea data-testid="spfu-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} placeholder="Catatan pemantauan / hasil evaluasi..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
          </div>
          <button data-testid="spfu-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan Tindak Lanjut"}</button>
        </div>
      </div>
    </div>
  );
}

function printLetter(w) {
  const win = window.open("", "_blank", "width=820,height=900");
  if (!win) return;
  win.document.write(`<html><head><title>${w.number}</title><style>
    body{font-family:Georgia,serif;margin:48px;color:#111;font-size:14px;line-height:1.7}
    .kop{text-align:center;border-bottom:3px double #7F1D2D;padding-bottom:12px;margin-bottom:24px}
    .kop h1{margin:0;font-size:22px;letter-spacing:2px;color:#7F1D2D}
    .kop p{margin:2px 0;font-size:11px;color:#555}
    h2{text-align:center;font-size:16px;text-decoration:underline;margin:0}
    .num{text-align:center;margin:4px 0 24px;font-size:12px}
    table.meta td{padding:2px 10px 2px 0;vertical-align:top;font-size:13px}
    .box{border:1px solid #ddd;padding:12px 16px;margin:12px 0;font-size:13px}
    .ttd{display:flex;justify-content:flex-end;margin-top:48px}
    .ttd div{text-align:center;font-size:13px}
    .ttd .nm{margin-top:64px;font-weight:bold;text-decoration:underline}
    @media print{body{margin:24px}}
  </style></head><body>
    <div class="kop"><h1>KIYANA FASHION ID</h1><p>Jl. Megamendung, Cirebon, Jawa Barat • HR Department</p></div>
    <h2>SURAT PERINGATAN ${w.level.replace("SP", "")}</h2>
    <div class="num">Nomor: ${w.number}</div>
    <p>Yang bertanda tangan di bawah ini, HR Kiyana Fashion ID, dengan ini memberikan <b>Surat Peringatan ${w.level.replace("SP", "")}</b> kepada:</p>
    <table class="meta">
      <tr><td>Nama</td><td>: <b>${w.user_name || "-"}</b></td></tr>
      <tr><td>Tanggal Terbit</td><td>: ${w.issued_date}</td></tr>
      <tr><td>Berlaku Sampai</td><td>: ${w.valid_until}</td></tr>
    </table>
    <div class="box"><b>Pelanggaran:</b><br/>${w.violation || "-"}</div>
    ${w.rule_basis ? `<div class="box"><b>Dasar Aturan:</b><br/>${w.rule_basis}</div>` : ""}
    ${w.improvement_target ? `<div class="box"><b>Target Perbaikan:</b><br/>${w.improvement_target}</div>` : ""}
    ${w.sanction_amount ? `<div class="box"><b>Sanksi Tambahan:</b> Potongan sanksi sebesar <b>Rp${Number(w.sanction_amount).toLocaleString("id-ID")}</b> sesuai dasar aturan perusahaan.</div>` : ""}
    <p>Apabila selama masa berlaku surat peringatan ini karyawan yang bersangkutan kembali melakukan pelanggaran, perusahaan akan memberikan tindakan lanjutan sesuai peraturan perusahaan.</p>
    <div class="ttd"><div>Cirebon, ${w.issued_date}<br/>HR Kiyana Fashion ID<div class="nm">${w.issued_by || "HR"}</div></div></div>
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 350);
}

export default function AdminWarnings() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("aktif");
  const [period, setPeriod] = useState("");
  const [level, setLevel] = useState("all");
  const [roleF, setRoleF] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [fuItem, setFuItem] = useState(null);
  const [revokeId, setRevokeId] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [delId, setDelId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/warnings").then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Gagal memuat surat peringatan"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const employees = data?.options?.employees || [];
  const roles = data?.options?.roles || [];
  const stats = data?.stats || { SP1: 0, SP2: 0, SP3: 0 };
  const sel = items.find((w) => w.id === selected) || null;

  const filtered = useMemo(() => {
    let arr = items;
    if (tab !== "semua") arr = arr.filter((w) => w.eff_status === tab);
    if (period) arr = arr.filter((w) => (w.issued_date || "").startsWith(period));
    if (level !== "all") arr = arr.filter((w) => w.level === level);
    if (roleF !== "all") arr = arr.filter((w) => w.user_role === roleF);
    const s = q.trim().toLowerCase();
    if (s) arr = arr.filter((w) => (w.user_name || "").toLowerCase().includes(s) || (w.number || "").toLowerCase().includes(s));
    return arr;
  }, [items, tab, period, level, roleF, q]);

  const history = useMemo(() => sel ? items.filter((w) => w.user_id === sel.user_id) : [], [items, sel]);
  const { paged: pagedSp, pager: spPager } = usePager(filtered);

  const doRevoke = async () => {
    if (revokeReason.trim().length < 5) return toast.error("Alasan pencabutan wajib diisi");
    setBusy(true);
    try {
      await api.post(`/admin/warnings/${revokeId}/revoke`, { reason: revokeReason.trim() });
      toast.success("SP dicabut");
      setRevokeId(null); setRevokeReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencabut SP"); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/warnings/${delId}`);
      toast.success("Draft SP dihapus");
      if (selected === delId) setSelected(null);
      setDelId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } finally { setBusy(false); }
  };

  const doPublish = async (w) => {
    try {
      await api.post(`/admin/warnings/${w.id}/publish`);
      toast.success(`${w.number} diterbitkan — karyawan kini bisa melihatnya`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerbitkan"); }
  };

  return (
    <div data-testid="sp-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Surat Peringatan</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola surat peringatan dan tindak lanjut karyawan.</p>
        </div>
        <button data-testid="sp-create-btn" onClick={() => { setEditItem(null); setShowForm(true); }} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5 transition-colors" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Buat SP</button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3">
        {["SP1", "SP2", "SP3"].map((lv) => (
          <div key={lv} data-testid={`sp-stat-${lv}`} className={`${card} p-4 flex items-center gap-3`}>
            <span className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${LV_DOT[lv]}`}><FileText className="w-5 h-5" /></span>
            <div>
              <div className="text-[11px] font-semibold text-slate-500">{lv} Aktif</div>
              <div className="font-display text-xl font-bold text-slate-900 leading-tight">{stats[lv]}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className={`${card} p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3`}>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Periode</label>
          <input data-testid="sp-filter-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inpL} />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Tingkat SP</label>
          <select data-testid="sp-filter-level" value={level} onChange={(e) => setLevel(e.target.value)} className={inpL}>
            <option value="all">Semua Tingkat</option>
            {["SP1", "SP2", "SP3"].map((lv) => <option key={lv} value={lv}>{lv}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Jabatan</label>
          <select data-testid="sp-filter-role" value={roleF} onChange={(e) => setRoleF(e.target.value)} className={inpL}>
            <option value="all">Semua Jabatan</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Karyawan</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="sp-filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ketik nama karyawan" className={`${inpL} pl-9`} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`sp-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-[#7F1D2D] text-[#7F1D2D] font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{t.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-4 items-start">
        {/* Daftar + Riwayat */}
        <div className="lg:col-span-3 space-y-4">
          <div className={`${card} overflow-hidden`}>
            <div className="px-5 pt-4 pb-2 font-display font-bold text-slate-900">Daftar Surat Peringatan</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="px-5 py-2.5 font-medium">Karyawan</th>
                    <th className="px-3 py-2.5 font-medium">Nomor / Tingkat</th>
                    <th className="px-3 py-2.5 font-medium">Berlaku Sampai</th>
                    <th className="px-3 py-2.5 font-medium">Status SP</th>
                    <th className="px-5 py-2.5 font-medium text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedSp.map((w) => (
                    <tr key={w.id} data-testid={`sp-row-${w.id}`} onClick={() => setSelected(w.id)}
                      className={`border-b border-slate-50 cursor-pointer transition-colors ${selected === w.id ? "bg-rose-50/50" : "hover:bg-slate-50/60"}`}>
                      <td className="px-5 py-3">
                        <div className="text-xs font-bold text-slate-800">{w.user_name}</div>
                        <div className="text-[10px] text-slate-400">{roles.find((r) => r.id === w.user_role)?.label || w.user_role}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-[11px] font-mono text-slate-600">{w.number}</div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${LV_CHIP[w.level]}`}>{w.level}</span>
                      </td>
                      <td className="px-3 py-3 text-[11px] text-slate-600">{formatDateID(w.valid_until)}</td>
                      <td className="px-3 py-3"><span data-testid={`sp-status-${w.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[w.eff_status]}`}>{ST_LABEL[w.eff_status]}</span></td>
                      <td className="px-5 py-3 text-right">
                        <button data-testid={`sp-detail-${w.id}`} className="h-7 px-3 rounded-lg border border-[#7F1D2D]/30 text-[#7F1D2D] text-[11px] font-semibold hover:bg-[#7F1D2D]/5 transition-colors">Detail</button>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr><td colSpan={5}><div data-testid="sp-empty" className="py-10 text-center text-xs text-slate-400">{data ? "Tidak ada SP pada filter ini." : "Memuat..."}</div></td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pager {...spPager} testid="sp-pager" />
          </div>

          {sel && (
            <div className={`${card} overflow-hidden`} data-testid="sp-history">
              <div className="px-5 pt-4 pb-2 flex items-center gap-2 font-display font-bold text-slate-900">
                <span className="w-7 h-7 rounded-full bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center"><History className="w-4 h-4" /></span>
                Riwayat SP • {sel.user_name}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="px-5 py-2.5 font-medium">Tanggal Terbit</th>
                    <th className="px-3 py-2.5 font-medium">Nomor SP</th>
                    <th className="px-3 py-2.5 font-medium">Tingkat</th>
                    <th className="px-5 py-2.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} data-testid={`sp-history-row-${h.id}`} className="border-b border-slate-50">
                      <td className="px-5 py-2.5 text-[11px] text-slate-600">{formatDateID(h.issued_date)}</td>
                      <td className="px-3 py-2.5 text-[11px] font-mono text-slate-600">{h.number}</td>
                      <td className="px-3 py-2.5"><span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${LV_CHIP[h.level]}`}>{h.level}</span></td>
                      <td className="px-5 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[h.eff_status]}`}>{h.eff_status === "kedaluwarsa" || h.eff_status === "dicabut" ? "Riwayat" : ST_LABEL[h.eff_status]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className={`lg:col-span-2 ${card} overflow-hidden`} data-testid="sp-detail-panel">
          {!sel ? (
            <div className="py-16 text-center text-xs text-slate-400 px-6">Pilih salah satu SP pada tabel untuk melihat detail lengkap.</div>
          ) : (
            <div>
              <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                <div className="font-display font-bold text-slate-900">Detail SP • {sel.user_name}</div>
                <div className="flex gap-1.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${LV_CHIP[sel.level]}`}>{sel.level}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ST_CHIP[sel.eff_status]}`}>{ST_LABEL[sel.eff_status]}</span>
                </div>
              </div>
              <div className="p-5 space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-y-1.5">
                  <span className="text-slate-400">Nomor</span><span className="font-mono font-semibold text-slate-700" data-testid="spd-number">{sel.number}</span>
                  <span className="text-slate-400">Diterbitkan</span><span className="text-slate-700">{sel.issued_by || "HR"}</span>
                  <span className="text-slate-400">Tanggal Terbit</span><span className="text-slate-700">{formatDateID(sel.issued_date)}</span>
                  <span className="text-slate-400">Berlaku Sampai</span><span className="text-slate-700">{formatDateID(sel.valid_until)}</span>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <div className="flex items-center gap-1.5 font-bold text-slate-800 mb-1"><FileText className="w-3.5 h-3.5 text-[#7F1D2D]" /> Pelanggaran</div>
                  <p className="text-slate-600">{sel.violation}</p>
                </div>

                {(sel.rule_basis || sel.attachment_name) && (
                  <div>
                    <div className="flex items-center gap-1.5 font-bold text-slate-800 mb-1"><Scale className="w-3.5 h-3.5 text-[#7F1D2D]" /> Dasar Aturan</div>
                    {sel.rule_basis && <p className="text-slate-600 mb-1.5">{sel.rule_basis}</p>}
                    {sel.attachment_name && (
                      <div className="flex items-center justify-between gap-2 border border-slate-200 rounded-xl px-3 py-2">
                        <span className="flex items-center gap-1.5 text-slate-600 min-w-0"><Paperclip className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> <span className="truncate">{sel.attachment_name}</span></span>
                        {sel.attachment_url && <a data-testid="spd-attachment" href={`${process.env.REACT_APP_BACKEND_URL}${sel.attachment_url}`} target="_blank" rel="noreferrer" className="font-semibold text-[#7F1D2D] hover:underline flex-shrink-0">Lihat</a>}
                      </div>
                    )}
                  </div>
                )}

                {sel.improvement_target && (
                  <div>
                    <div className="flex items-center gap-1.5 font-bold text-slate-800 mb-1"><Target className="w-3.5 h-3.5 text-[#7F1D2D]" /> Target Perbaikan</div>
                    <p className="text-slate-600">{sel.improvement_target}</p>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-1.5 font-bold text-slate-800 mb-1"><Coins className="w-3.5 h-3.5 text-[#7F1D2D]" /> Sanksi Tambahan</div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Potongan Sanksi SP</span>
                    <span className="font-bold text-slate-800" data-testid="spd-sanction">{sel.sanction_amount ? formatIDR(sel.sanction_amount) : "—"}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5">Catatan • Sesuai dasar aturan perusahaan — tidak otomatis memotong payroll.</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-slate-400">Status Payroll</span>
                    <span data-testid="spd-payroll-status" className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">Belum Diterapkan</span>
                  </div>
                </div>

                {sel.follow_up && (
                  <div className="border-t border-slate-100 pt-3">
                    <div className="flex items-center gap-1.5 font-bold text-slate-800 mb-1.5"><ClipboardCheck className="w-3.5 h-3.5 text-[#7F1D2D]" /> Tindak Lanjut HR</div>
                    <div className="grid grid-cols-2 gap-y-1.5">
                      <span className="text-slate-400">Status</span>
                      <span><span data-testid="spd-fu-status" className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${FU_CHIP[sel.follow_up.status] || "bg-slate-100 text-slate-500"}`}>{FU_LABEL[sel.follow_up.status] || sel.follow_up.status}</span></span>
                      {sel.follow_up.next_eval_date && <><span className="text-slate-400">Evaluasi Berikutnya</span><span className="text-slate-700">{formatDateID(sel.follow_up.next_eval_date)}</span></>}
                      {sel.follow_up.note && <><span className="text-slate-400">Catatan</span><span className="text-slate-700">{sel.follow_up.note}</span></>}
                    </div>
                  </div>
                )}
                {sel.eff_status === "dicabut" && sel.revoked_reason && (
                  <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">Dicabut oleh {sel.revoked_by || "HR"} — {sel.revoked_reason}</div>
                )}

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button data-testid="spd-letter-btn" onClick={() => printLetter(sel)} className="h-10 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#7F1D2D]/5 transition-colors"><Printer className="w-3.5 h-3.5" /> Lihat Surat</button>
                  <button data-testid="spd-followup-btn" onClick={() => setFuItem(sel)} disabled={sel.status === "draft"} className="h-10 rounded-xl text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40 transition-colors" style={{ background: MAROON }}><ClipboardCheck className="w-3.5 h-3.5" /> Catat Tindak Lanjut</button>
                </div>
                <div className="flex items-center gap-2">
                  {sel.status === "draft" && (
                    <>
                      <button data-testid="spd-publish-btn" onClick={() => doPublish(sel)} className="flex-1 h-9 rounded-xl bg-emerald-600 text-white text-[11px] font-semibold">Terbitkan SP</button>
                      <button data-testid="spd-edit-btn" onClick={() => { setEditItem(sel); setShowForm(true); }} className="w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:text-[#7F1D2D] flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid="spd-del-btn" onClick={() => setDelId(sel.id)} className="w-9 h-9 rounded-xl border border-slate-200 text-slate-400 hover:text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                  {sel.status === "aktif" && (
                    <>
                      <button data-testid="spd-edit-btn" onClick={() => { setEditItem(sel); setShowForm(true); }} className="flex-1 h-9 rounded-xl border border-slate-200 text-slate-500 text-[11px] font-semibold hover:text-[#7F1D2D] flex items-center justify-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit SP</button>
                      <button data-testid="spd-revoke-btn" onClick={() => setRevokeId(sel.id)} className="flex-1 h-9 rounded-xl border border-red-200 text-red-500 text-[11px] font-semibold hover:bg-red-50">Cabut SP</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showForm && <SpForm initial={editItem} employees={employees} onClose={() => setShowForm(false)} onSaved={load} />}
      {fuItem && <FollowUpModal item={fuItem} onClose={() => setFuItem(null)} onSaved={load} />}

      {revokeId && (
        <div data-testid="sp-revoke-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" onClick={() => setRevokeId(null)}>
          <div className="bg-white w-full max-w-sm rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-bold text-slate-900">Cabut Surat Peringatan?</h3>
            <p className="text-xs text-slate-500">SP yang dicabut tidak lagi berlaku dan tidak tampil di portal karyawan. Tindakan ini tercatat di audit log.</p>
            <textarea data-testid="sp-revoke-reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} rows={2} placeholder="Alasan pencabutan (wajib)..." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#7F1D2D]" />
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="sp-revoke-no" onClick={() => setRevokeId(null)} className="h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
              <button data-testid="sp-revoke-yes" onClick={doRevoke} disabled={busy} className="h-10 rounded-xl bg-red-500 text-white text-xs font-semibold disabled:opacity-40">{busy ? "Memproses..." : "Ya, Cabut SP"}</button>
            </div>
          </div>
        </div>
      )}
      {delId && (
        <div data-testid="sp-del-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" onClick={() => setDelId(null)}>
          <div className="bg-white w-full max-w-xs rounded-2xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-bold text-slate-900">Hapus Draft SP?</h3>
            <p className="text-xs text-slate-500">Draft yang dihapus tidak bisa dikembalikan.</p>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="sp-del-no" onClick={() => setDelId(null)} className="h-10 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold">Batal</button>
              <button data-testid="sp-del-yes" onClick={doDelete} disabled={busy} className="h-10 rounded-xl bg-red-500 text-white text-xs font-semibold disabled:opacity-40">{busy ? "Menghapus..." : "Ya, Hapus"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
